<template>
  <div>
    <form v-if="allowFileUpload" enctype="multipart/form-data" novalidate>
      <asc-label size="sm" :value="title" :color="getTitleColor"></asc-label>
      <asc-tooltip :tooltip="getTooltip" top :tooltip-length="0">
        <div
          ref="ascFileUploadDropbox"
          :class="[
            'asc-file-upload--dropbox',
            disableUpload || disabled ? 'asc-file-upload--disabled' : null,
            readonly ? 'asc-file-upload--readonly' : null,
          ]"
        >
          <input
            ref="ascFileUploadInput"
            type="file"
            class="asc-file-upload--input-file"
            :disabled="disableUpload || disabled"
            @change="handleFilesChange($event.target.files)"
          />
          <div class="asc-file-upload--message-container">
            <div class="icon">
              <v-icon size="18" :color="getIconColor">
                {{ disabled ? 'mdi-cloud-off-outline' : 'mdi-cloud-upload' }}
              </v-icon>
            </div>
            <div class="text">
              <span>{{ infoText }}</span>
            </div>
            <div class="text-accent">
              <span>{{ linkText }}</span>
            </div>
          </div>
        </div>
      </asc-tooltip>
      <template v-if="hasErrors">
        <asc-file-upload-errors
          :items="fileUploadErrors"
          @cancelFileUpload="handleCancelFromError"
          @retryFileUpload="handleRetryFileUpload"
        ></asc-file-upload-errors>
      </template>
    </form>

    <asc-file-upload-file-list
      :items="model"
      :show-file-visibility-toggle="showFileVisibilityToggle"
      :allow-file-remove="allowFileRemove"
      :on-remove-file="handleRemoveFile"
      :show-file-remove-notification="showFileRemoveNotification"
      :remove-file-notification-count-down="removeFileNotificationCountDown"
      :allow-file-download="allowFileDownload"
      :on-download-file="handleDownloadFile"
      :disabled="disabled"
      :viewable="viewable"
      :handle-update-attachment-caption="handleUpdateAttachmentCaption"
      :can-edit-attachment-captions="canEditAttachmentCaptions"
      @toggleFileVisibility="handleToggleFileVisibility"
    ></asc-file-upload-file-list>
  </div>
</template>

<script>
import AscLabel from '@/components/atoms/AscLabel'
import AscTooltip from '@/components/atoms/AscTooltip'
import AscFileUploadErrors from './AscFileUploadErrors'
import AscFileUploadFileList from './AscFileUploadFileList'

import { parseFile } from '@/utilities/file'

import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscFileUploadInput',

  components: {
    AscLabel,
    AscFileUploadErrors,
    AscFileUploadFileList,
    AscTooltip,
  },

  props: {
    acceptedFileProperties: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: Array,
      default: () => [],
    },
    infoText: {
      type: String,
      default: 'Drag and Drop or',
    },
    linkText: {
      type: String,
      default: 'Choose File',
    },
    maxFileUploads: {
      type: Number,
      default: 10,
    },
    showFileVisibilityToggle: {
      type: Boolean,
      default: false,
    },
    allowFileUpload: {
      type: Boolean,
      default: true,
    },
    onUploadFile: {
      type: Function,
      default: () => {},
    },
    allowFileRemove: {
      type: Boolean,
      default: true,
    },
    onRemoveFile: {
      type: Function,
      default: () => {},
    },
    showFileRemoveNotification: {
      type: Boolean,
      default: true,
    },
    removeFileNotificationCountDown: {
      type: Number,
      default: 8,
    },
    allowFileDownload: {
      type: Boolean,
      default: true,
    },
    onDownloadFile: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    uploadError: {
      type: Array,
      default: () => [],
    },
    viewable: {
      type: Boolean,
      default: false,
    },
    canEditAttachmentCaptions: {
      type: Boolean,
      default: false,
    },
    handleUpdateAttachmentCaption: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      viewModel: this.value,
      fileUploadErrors: [],
      filesToRetry: [],
    }
  },

  computed: {
    getTooltip() {
      if (this.disableUpload || this.disabled || this.readonly) return ''

      return this.allowedFileTypes && this.allowedFileTypes.length
        ? `${this.maxFileUploads} Maximum (${this.allowedFileTypes
            .split(',')
            .join(', ')})`
        : `${this.maxFileUploads} Maximum`
    },
    allowedFileTypes() {
      return this.acceptedFileProperties?.allowedFileTypes
    },
    disableUpload() {
      return (
        !this.allowedFileTypes || this.viewModel.length >= this.maxFileUploads
      )
    },
    model: {
      get() {
        return this.viewModel
      },
      set(newValue) {
        this.viewModel = newValue
      },
    },
    hasErrors() {
      return this.fileUploadErrors.length > 0
    },
    getTitleColor() {
      return variables.gray700
    },
    getIconColor() {
      return variables.gray500
    },
  },

  watch: {
    value(newValue) {
      this.viewModel = newValue
    },
    uploadError(newValue) {
      if (newValue.length) {
        // eslint-disable-next-line array-callback-return
        newValue.map(file => {
          this.fileUploadErrors.push({
            message: `An error occurred while uploading ${file.name}. Please try again.`,
            file: file,
          })
          this.viewModel = this.viewModel.filter(fileObj => {
            return fileObj.displayName !== file.name
          })
        })
      }
    },
  },

  mounted() {
    if (this.allowFileUpload === true) {
      this.$refs.ascFileUploadInput.addEventListener(
        'dragover',
        this.handleFileDragOver,
        false,
      )
      this.$refs.ascFileUploadInput.addEventListener(
        'dragleave',
        this.handleFileDragLeave,
        false,
      )
      this.$refs.ascFileUploadInput.addEventListener(
        'drop',
        this.handleFileDragLeave,
        false,
      )
    }
  },

  beforeDestroy() {
    if (this.allowFileUpload === true) {
      this.$refs.ascFileUploadInput.removeEventListener(
        'dragover',
        this.handleFileDragOver,
      )
      this.$refs.ascFileUploadInput.removeEventListener(
        'dragleave',
        this.handleFileDragLeave,
      )
      this.$refs.ascFileUploadInput.removeEventListener(
        'drop',
        this.handleFileDragLeave,
      )
    }
  },

  methods: {
    handleFilesChange(fileList) {
      if (!fileList.length) return
      if (fileList.length + this.value.length <= this.maxFileUploads) {
        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i]
          try {
            // stash a reference to the file so that the upload can be
            // retried
            this.filesToRetry.push(file)
            parseFile(file, this.viewModel, this.acceptedFileProperties)
            this.viewModel.push({
              lastModified: file.lastModified,
              displayName: file.name,
              fileSizeInBytes: file.size,
              type: file.type,
              loading: true,
            })
            if (this.onUploadFile(file)) {
              this.onUploadFile(file)
              // remove this specific file from the files to retry array
              // (this fixes a bug when you're uploading multiple files)
              this.filesToRetry = this.filesToRetry.filter(
                fileToRetryFilterObj => {
                  return fileToRetryFilterObj !== file.name
                },
              )
            }
          } catch (error) {
            // need to pass a reference to the file down
            // to the error component so if we restart or cancel
            // we know which upload to restart or cancel
            // if the file exceeds the max file size, we pass
            // null for the file to hide the retry button.  Nothing to retry at that point
            let fileToPush = file
            if (
              Math.ceil(file.size / 1024 / 1024) >
              this.acceptedFileProperties.maxFileSizeInMB
            ) {
              fileToPush = null
            }
            this.fileUploadErrors.push({
              message: error.message,
              file: fileToPush,
            })
          }
        }
      } else {
        this.fileUploadErrors.push({
          // for the max upload error, send a null file argument
          // since we don't restart or cancel that
          message: `File upload exceeds ${this.maxFileUploads} item limit.`,
          file: null,
        })
      }
    },
    handleRemoveFile(file) {
      this.viewModel = this.viewModel.filter(f => {
        return f !== file
      })

      if (this.onRemoveFile) {
        this.onRemoveFile(file)
        // here we "set" the file to be invisible
        // (you're removing it, so technically it is)
        // so that this event will be triggered and the visible count will work
        file.isExternallyVisible = false
        this.$emit('handleToggleFileVisibility', { remove: true, file })
      }
    },
    handleCancelFromError(file) {
      // pass reference to file back through so we can remove it
      this.fileUploadErrors = this.fileUploadErrors.filter(item => {
        return file?.name ? item.file.name !== file.name : false
      })
      // need to reset the form field value to blank so that change action
      // will fire if you try the same file again
      this.$refs.ascFileUploadInput.value = ''
    },
    handleRetryFileUpload(file) {
      const whichFileToRetry = this.filesToRetry.filter(whichFile => {
        return whichFile.name === file.name
      })
      if (whichFileToRetry.length) {
        this.onUploadFile(whichFileToRetry[0])
        // if you retry a file, clear its error
        this.fileUploadErrors = this.fileUploadErrors.filter(
          fileUploadErrorObj => {
            return fileUploadErrorObj.file.name !== file.name
          },
        )
        // remove this specific file from the files to retry array
        // (this fixes a bug when you're uploading multiple files)
        this.filesToRetry = this.filesToRetry.filter(fileToRetryFilterObj => {
          return fileToRetryFilterObj !== file.name
        })
      }
    },
    handleDownloadFile(file) {
      if (this.onDownloadFile) {
        this.onDownloadFile({ label: file.displayName, id: file.id })
      }
    },
    handleFileDragOver() {
      this.$refs.ascFileUploadDropbox.classList.add(
        'asc-file-upload--dropbox-drag',
      )
    },
    handleFileDragLeave() {
      this.$refs.ascFileUploadDropbox.classList.remove(
        'asc-file-upload--dropbox-drag',
      )
    },
    handleToggleFileVisibility(file) {
      this.viewModel.forEach((fileObj, index) => {
        if (fileObj.displayName === file.displayName) {
          this.viewModel[index] = {
            ...fileObj,
            isExternallyVisible: file.isExternallyVisible,
          }
        }
      })
      this.$emit('handleToggleFileVisibility', file)
    },
  },
}
</script>

<style lang="scss">
.asc-file-upload--dropbox {
  outline: 1px dashed $gray-500;
  background-color: $gray-0;
  margin: 2px;
  height: 46px;
  cursor: pointer;
  transition: 0.2s linear;
  position: relative;
  min-width: 225px;

  .asc-file-upload--input-file {
    opacity: 0; /* invisible but it's there! */
    width: 100%;
    height: 46px;
    position: absolute;
    cursor: pointer;
  }

  .asc-file-upload--message-container {
    display: flex;
    align-items: center;
    margin-left: 8px;
    height: 46px;
    line-height: 46px;
    background: transparent;

    .icon {
      margin-top: -2px;
    }

    .text {
      padding-left: 8px;
      color: $gray-700;
    }

    .text-accent {
      padding-left: 4px;
      color: $accent-700;
    }
  }

  &.asc-file-upload--disabled {
    cursor: not-allowed;

    .asc-file-upload--input-file {
      cursor: not-allowed;
    }
  }

  &.asc-file-upload--readonly {
    display: none;
  }

  &:hover {
    outline: 1px solid $accent-500;
    background-color: $accent-0;
  }
}

.asc-file-upload--dropbox-drag:not(.asc-file-upload--disabled) {
  background-color: $accent-100;
  outline: 2px dashed $accent-500;
}
</style>
